/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import { getSpecifications, readProductField, SPECIFICATION_TITLE_FIELD } from '@/lib/product/product-record-fields';

/**
 * "What's Included" — the product record's `specification1…5` rendered as a bullet list. Empty
 * specification fields are skipped, and the whole section renders nothing when none are populated.
 * The heading comes from `specificationTitle`, defaulting to "What's Included".
 */
export default function WhatsIncluded({
    product,
}: {
    product: ShopperProducts.schemas['Product'];
}): ReactElement | null {
    const bullets = getSpecifications(product);

    if (bullets.length === 0) {
        return null;
    }

    const title = readProductField(product, SPECIFICATION_TITLE_FIELD) ?? "What's Included";

    return (
        <section data-slot="pdp-whats-included" className="mb-10 space-y-3">
            <h2 className="font-condensed text-[22px] font-bold uppercase leading-[0.95] text-card-foreground">
                {title}
            </h2>
            <ul className="list-disc space-y-1 pl-6 text-base leading-[1.2] text-card-foreground">
                {bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                ))}
            </ul>
        </section>
    );
}
